import React from 'react';
import { SearchX, ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { SEOHead } from '@/components/seo/SEOHead';
import LocalizedLink from '@/i18n/LocalizedLink';
import { Button } from '@/components/ui/button';

export const NotFoundState = ({
  title,
  description,
  primaryTo = '/events',
  primaryLabel,
  secondaryTo = '/',
  secondaryLabel,
}) => {
  const { t } = useTranslation();

  return (
    <>
      <SEOHead
        title={title || t('notFoundPage.title')}
        description={description || t('notFoundPage.description')}
        robotsContent="noindex, nofollow, nosnippet"
        includeHreflang={false}
        includeCanonical={false}
        includeSocialMeta={false}
      />

      <section className="min-h-[70vh] bg-[radial-gradient(circle_at_top,_rgba(249,115,22,0.16),_transparent_35%),linear-gradient(180deg,_#fffaf5_0%,_#ffffff_48%,_#f8fafc_100%)] px-4 py-20">
        <div className="mx-auto max-w-3xl rounded-[2rem] border border-orange-100 bg-white/90 px-8 py-14 text-center shadow-[0_20px_80px_rgba(15,23,42,0.08)] backdrop-blur-sm sm:px-12">
          <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-orange-100 text-orange-600">
            <SearchX className="h-8 w-8" />
          </div>

          <p className="mb-3 text-xs font-semibold uppercase tracking-[0.3em] text-orange-600" data-testid="not-found-eyebrow">
            {t('notFoundPage.eyebrow')}
          </p>
          <h1 className="mb-4 text-4xl font-bold text-slate-900 sm:text-5xl" data-testid="not-found-title">
            {title || t('notFoundPage.title')}
          </h1>
          <p className="mx-auto mb-10 max-w-2xl text-base leading-7 text-slate-600" data-testid="not-found-description">
            {description || t('notFoundPage.description')}
          </p>

          <div className="flex flex-col justify-center gap-4 sm:flex-row">
            <LocalizedLink to={primaryTo} data-testid="not-found-primary-link">
              <Button className="min-w-[180px] bg-orange-600 text-white hover:bg-orange-700" data-testid="not-found-primary-button">
                {primaryLabel || t('notFoundPage.eventsCta')}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </LocalizedLink>

            <LocalizedLink to={secondaryTo} data-testid="not-found-secondary-link">
              <Button variant="outline" className="min-w-[180px] border-slate-300 text-slate-700 hover:bg-slate-50" data-testid="not-found-secondary-button">
                {secondaryLabel || t('notFoundPage.homeCta')}
              </Button>
            </LocalizedLink>
          </div>
        </div>
      </section>
    </>
  );
};